import React, { Component } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { set, get, find, reject, map, isEmpty, sumBy } from 'lodash/fp';
import PropTypes from 'prop-types';
import {
  Content,
  Loader,
  ButtonGroup,
  Dropdown,
  ButtonToolbar,
  Button,
  Icon,
  Panel
} from 'rsuite';
import {
  ItemType,
  PackageType
} from '../../../../constants/inventory/InventoryTypes';
import InventoryActions from '../../../../constants/inventory/InventoryActions';
import IconByAdjustAction from '../../../../constants/inventory/IconByAdjustAction';
import {
  InventoryItemType,
  InventoryPackageType,
  InventoryItemSkuType,
  InventoryAvailabilityType
} from '../../../../interfaces/inventory';
import * as PERMISSIONS from '../../../../constants/permissions/inventory';
import {
  viewAccess,
  modifyAccess,
  fullAccess
} from '../../../../helpers/permission';
import AccessControl from '../../../../components/AccessControl';
import AccessDenied from '../../../../components/AccessDenied';
import Breadcrumb from '../../../../components/Breadcrumb';
import ActionIconLink from '../../../../components/inventory/ActionIconLink';
import DeleteInventoryItemModal from '../../../../components/inventory/DeleteItemModal';
import InventoryPageLayout from '../../../../containers/inventory/PageLayout';
import InventoryItemDetails from '../../../../components/inventory/ItemDetails';
import InventoryAuditLogListForItem from '../../../../containers/inventory/AuditLogListForItem';
import createItemPathFor from '../../../../helpers/createItemPathFor';
import AuditLogSearchForItem from '../../../../containers/inventory/AuditLogSearchForItem';

const permissionByType = {
  [ItemType]: PERMISSIONS.ITEM,
  [PackageType]: PERMISSIONS.PACKAGE
};

const NavLink = props => <Dropdown.Item componentClass={Link} {...props} />;

export default class ViewInventoryItem extends Component {
  static propTypes = {
    params: PropTypes.shape({ id: PropTypes.string }).isRequired,
    type: PropTypes.oneOf([ItemType, PackageType]).isRequired,
    item: PropTypes.oneOfType([InventoryItemType, InventoryPackageType]),
    skus: PropTypes.arrayOf(InventoryItemSkuType),
    packageItems: PropTypes.arrayOf(PropTypes.shape({})),
    availability: PropTypes.arrayOf(InventoryAvailabilityType),
    actionOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    isFetching: PropTypes.bool.isRequired,
    isFetchingSkus: PropTypes.bool,
    isFetchingPackageItems: PropTypes.bool,
    isFetchingAvailability: PropTypes.bool,
    isFetchingActionOptions: PropTypes.bool.isRequired,
    isDeleting: PropTypes.bool.isRequired,
    isDeleted: PropTypes.bool.isRequired,
    onInit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onUnmount: PropTypes.func
  };

  static defaultProps = {
    item: null,
    skus: null,
    packageItems: [],
    availability: null,
    isFetchingSkus: false,
    isFetchingPackageItems: false,
    isFetchingAvailability: false,
    onUnmount: () => {}
  };

  constructor(props) {
    super(props);
    // eslint-disable-next-line react/no-unused-state
    this.state = { isInit: false, type: null, showConfirmDelete: false };
  }

  static getDerivedStateFromProps({ item }, state) {
    if (item && !state.type) {
      return set('type', item.itemType, state);
    }
    return state;
  }

  componentDidMount() {
    const { isInit } = this.state;
    if (!isInit) {
      const { onInit } = this.props;
      onInit();
      this.setState({ isInit: true });
    }
  }

  componentDidUpdate({ isDeleted: prevIsDeleted, params: prevParams }) {
    const { isDeleted, params, onInit } = this.props;
    if (!prevIsDeleted && isDeleted) {
      this.onItemDeleted();
    }
    // Is switching item page (via barcode scan)
    if (params && prevParams && params.id !== prevParams.id) {
      onInit();
    }
  }

  componentWillUnmount() {
    const { onUnmount } = this.props;
    onUnmount();
  }

  get isPackage() {
    const { item } = this.props;
    return item.itemType === PackageType;
  }

  onItemDeleted = () => {
    this.handleHideDeleteModal();
  };

  handleShowDeleteModal = () => {
    this.setState({ showConfirmDelete: true });
  };

  handleHideDeleteModal = () => {
    this.setState({ showConfirmDelete: false });
  };

  handlConfirmDelete = () => {
    const { item, onDelete } = this.props;
    onDelete(item);
  };

  renderBreadcrumb() {
    const { item } = this.props;
    return (
      <Breadcrumb
        items={[
          {
            path: createItemPathFor('list', item),
            label: this.isPackage ? 'Packages' : 'Items'
          },
          { label: get('name', item) }
        ]}
      />
    );
  }

  renderActionsToolbar() {
    const { ADD, REMOVE, MOVE } = InventoryActions;
    const { actionOptions, item, availability } = this.props;
    const permission = permissionByType[item.itemType];
    const hasQty = sumBy('quantity', availability) > 0;
    const addQtyAction = find({ value: ADD }, actionOptions);
    const deductQtyAction = find({ value: REMOVE }, actionOptions);
    const moreActionOptions = reject(
      ({ value: action }) => action === ADD || action === REMOVE,
      actionOptions
    );

    return (
      <ButtonToolbar style={{ marginBottom: 20 }}>
        {addQtyAction && (
          <AccessControl allowOnlyFor={modifyAccess(permission)}>
            <ActionIconLink
              action={ADD}
              label="Quantity"
              to={createItemPathFor('adjust', item, { action: ADD })}
            />
          </AccessControl>
        )}
        {deductQtyAction && (
          <AccessControl allowOnlyFor={modifyAccess(permission)}>
            <ActionIconLink
              action={REMOVE}
              label="Quantity"
              to={createItemPathFor('adjust', item, { action: REMOVE })}
              disabled={!hasQty}
            />
          </AccessControl>
        )}
        <AccessControl allowOnlyFor={modifyAccess(permission)}>
          <ActionIconLink
            label="Edit"
            icon="pencil"
            to={createItemPathFor('edit', item)}
          />
        </AccessControl>
        <AccessControl allowOnlyFor={fullAccess(permission)}>
          <ButtonGroup>
            <Button
              size="sm"
              onClick={this.handleShowDeleteModal}
              disabled={hasQty}
            >
              <Icon icon="trash" /> Delete
            </Button>
          </ButtonGroup>
        </AccessControl>
        {!isEmpty(moreActionOptions) && (
          <AccessControl allowOnlyFor={modifyAccess(permission)}>
            <Dropdown title="More...">
              {map(
                ({ label, value: action }) => (
                  <NavLink
                    key={`action-${action}`}
                    to={createItemPathFor('adjust', item, { action })}
                    disabled={action === MOVE && !hasQty}
                  >
                    <Icon icon={IconByAdjustAction[action]} /> {label}
                  </NavLink>
                ),
                moreActionOptions
              )}
            </Dropdown>
          </AccessControl>
        )}
      </ButtonToolbar>
    );
  }

  renderDeleteConfirmationModal() {
    const { item, isDeleting } = this.props;
    const { showConfirmDelete } = this.state;
    return (
      <DeleteInventoryItemModal
        show={showConfirmDelete}
        onClose={this.handleHideDeleteModal}
        onConfirm={this.handlConfirmDelete}
        item={item}
        isDeleting={isDeleting}
      />
    );
  }

  renderItemAuditLogs() {
    const { item } = this.props;
    return (
      <AccessControl allowOnlyFor={viewAccess(PERMISSIONS.AUDIT)}>
        <Panel header="Audit Logs" bordered bodyFill style={{ marginTop: 20 }}>
          <div style={{ margin: '0 20px 20px' }}>
            <AuditLogSearchForItem item={item} />
          </div>
          <InventoryAuditLogListForItem item={item} />
        </Panel>
      </AccessControl>
    );
  }

  renderRedirectToItemList() {
    const { type } = this.props;
    return <Redirect to={createItemPathFor('list', { itemType: type })} />;
  }

  render() {
    const {
      item,
      skus,
      packageItems,
      availability,
      actionOptions,
      isFetching,
      isFetchingSkus,
      isFetchingPackageItems,
      isFetchingAvailability,
      isFetchingActionOptions,
      isDeleted
    } = this.props;

    const isLoading =
      isFetching ||
      isFetchingSkus ||
      isFetchingPackageItems ||
      isFetchingAvailability ||
      isFetchingActionOptions;

    if (isDeleted) {
      return this.renderRedirectToItemList();
    }

    if (isLoading || !item) {
      return <Loader backdrop vertical />;
    }

    return (
      <InventoryPageLayout>
        <AccessControl
          allowOnlyFor={viewAccess(permissionByType[item.itemType])}
          fallback={<AccessDenied />}
        >
          {this.renderBreadcrumb()}
          <Content>
            {!isEmpty(actionOptions) && this.renderActionsToolbar()}
            {
              <InventoryItemDetails
                item={item}
                skus={skus}
                packageItems={packageItems}
                availability={availability}
              />
            }
            {item && !this.isPackage && this.renderItemAuditLogs()}
          </Content>
          {this.renderDeleteConfirmationModal()}
        </AccessControl>
      </InventoryPageLayout>
    );
  }
}
